import React from "react";
import { motion } from "framer-motion";
import { Plus, Shield } from "lucide-react";
import { SITE } from "@/data/site";
import { czk, bundleFullPrice, bundleSavings, preselect } from "@/lib/price";

const POPULAR = ["ceramic", "upholstery", "headlights"];

function BundleCard({ b, primary }) {
  return (
    <button
      type="button"
      onClick={() => preselect({ bundle: b.key })}
      data-testid={`pricing-bundle-add-${b.key}`}
      className={`group relative text-left p-6 flex flex-col justify-between min-h-[200px] transition-colors ${
        primary
          ? "bg-brand-accent text-black hover:bg-brand-accentHover"
          : "bg-brand-surface text-white border border-brand-border hover:border-brand-accent"
      }`}
    >
      <span
        className={`absolute top-0 right-0 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 ${
          primary ? "bg-black text-brand-accent" : "bg-brand-accent text-black"
        }`}
      >
        Ušetříte {czk(bundleSavings(b))}
      </span>
      <span>
        <span className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] ${primary ? "opacity-70" : "text-brand-accent"}`}>
          <Shield size={14} /> Balíček · {b.tag}
        </span>
        <span className="block mt-2 font-display font-black text-2xl">{b.label}</span>
        <span className={`block mt-1 text-sm leading-snug ${primary ? "opacity-80" : "text-brand-muted"}`}>{b.desc}</span>
      </span>
      <span className="mt-5 flex items-end justify-between gap-3">
        <span>
          <span className={`text-sm line-through ${primary ? "opacity-60" : "text-brand-muted"}`}>{czk(bundleFullPrice(b))}</span>
          <span className={`block font-display font-black text-3xl leading-none ${primary ? "" : "text-brand-accent"}`}>
            +{czk(b.price)}
          </span>
        </span>
        <span
          className={`inline-flex items-center gap-2 font-semibold px-4 py-2.5 text-sm ${
            primary ? "bg-black text-brand-accent" : "bg-brand-accent text-black"
          }`}
        >
          Přidat k programu <Plus size={14} className="transition-transform group-hover:rotate-90" />
        </span>
      </span>
    </button>
  );
}

export function PricingAddons() {
  const popular = POPULAR.map((k) => SITE.addons.find((a) => a.key === k));

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.15 }}
      className="mt-12"
      data-testid="pricing-addons"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">Nejčastěji přidávané</div>
          <h3 className="mt-2 font-display font-black text-2xl lg:text-3xl">
            Chcete, aby lesk <span className="italic font-light">vydržel roky?</span>
          </h3>
        </div>
        <p className="text-sm text-brand-muted max-w-sm">
          Přidáte jedním klikem přímo do rezervace. Bez volání, bez dohadování ceny. SUV a velké
          vozy +{SITE.suvSurchargePct} %.
        </p>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-3">
        {SITE.bundles.map((b, i) => (
          <BundleCard key={b.key} b={b} primary={i === 0} />
        ))}
      </div>

      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        {popular.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => preselect({ addon: a.key })}
            data-testid={`pricing-addon-add-${a.key}`}
            className="group text-left bg-brand-surface border border-brand-border hover:border-brand-accent p-5 flex flex-col justify-between transition-colors"
          >
            <span>
              <span className="block font-semibold text-white text-sm">{a.label}</span>
              <span className="block mt-1 text-xs text-brand-muted leading-snug">{a.desc}</span>
            </span>
            <span className="mt-5 flex items-center justify-between">
              <span className="font-display font-black text-xl text-brand-accent">+{czk(a.price)}</span>
              <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-brand-muted group-hover:text-brand-accent transition-colors">
                Přidat <Plus size={12} />
              </span>
            </span>
          </button>
        ))}
      </div>
    </motion.div>
  );
}
